import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import API from "../../Api/api";
import { Box, TableBody } from "@mui/material";
import { Button, Divider, TextField, Typography } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import moment from "moment";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const exportToExcel = (data, periodStart, periodEnd) => {
  const headers = [
    "COST",
    ...data?.map((item) => item.category.toUpperCase()),
    "TOTAL",
  ];

  // Prepare rows for each field
  const rows = [
    ["Cost At Start", ...data.map((item) => item.costAtStart)],
    ["Cost Of Addition", ...data.map((item) => item.costOfAddition)],
    ["Cost Of Revalued Asset", ...data.map((item) => item.costOfRevalue)],
    [
      "Original Cost Before Revaluation",
      ...data.map((item) => item.costOfOriginal),
    ],
    ["Cost Of Disposal", ...data.map((item) => item.costOfDisposal)],
    ["Total", ...data.map((item) => item.totalCost)],

    // Depreciation Data
    ["Depreciation At Start", ...data.map((item) => item.depreciationAtStart)],
    [
      "Depreciation Charge For Period",
      ...data.map((item) => item.depreciationOfPeriod),
    ],
    [
      "Accumulated Depreciation for Revalue",
      ...data.map((item) => item.accumulatedDepreciationOfRevalue),
    ],
    [
      "Accumulated Depreciation Of Disposal",
      ...data.map((item) => item.depreciationOfDisposal),
    ],
    ["Total Depreciation", ...data.map((item) => item.totalDepreciation)],
    ["Carrying Amount", ...data.map((item) => item.carryingAmount)],
  ];

  // Add totals at the end of each row
  rows.forEach((row) =>
    row.push(row.slice(1).reduce((sum, val) => sum + val, 0))
  );

  // 🟩 Add report information at the top
  const infoRows = [
    ["", "PROPERTY, PLANT AND EQUIPMENT REPORT"], // Title Row
    ["Period Start:", periodStart || ""],
    ["Period End:", periodEnd || ""],
    [], // Empty row before the table
  ];

  // Combine all rows
  const sheetData = [...infoRows, headers, ...rows];

  // Create worksheet and workbook
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "PPE Data");

  // Export
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const file = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(file, "PPE_Data.xlsx");
};

export default function PPE() {
  const mode_state = useSelector((state) => state.mode);
  const [mode, setMode] = React.useState(mode_state);
  React.useEffect(() => {
    setMode(mode_state);
  }, [mode_state]);

  const [data, setData] = React.useState([]);

  const [ppeDate, setPpeDate] = React.useState({
    start_date: "",
    end_date: "",
  });

  const handleFetch = async () => {
    let fetchingId;
    try {
      if (!ppeDate.start_date || !ppeDate.end_date) {
        toast.error("Both date fields are required");
        return;
      }
      fetchingId = toast.loading("generating ppe report...");
      const { data } = await API.get(
        `/api/v1/company-assets/ppe/ppe-section?approved[eq]=true&purchase_date[lte]=${ppeDate.end_date}`
      );
      const filteredData = data.filter(
        (d) =>
          d.category !== "intangibles" && d.category !== "right-of-use asset"
      );
      const RightOfUseData = data.filter(
        (d) =>
          d.category === "right-of-use asset" &&
          moment(d.exhaustion_date) >= moment(ppeDate.start_date)
      );

      const allData = [...filteredData, ...RightOfUseData];
      setData(allData);
      toast.remove(fetchingId);
      toast.success("Data Loaded");
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
    }
  };

  function filterAndCalculate(assets, startDate, endDate) {
    // Convert input dates to Date objects
    const start = moment(startDate);
    // Group and calculate costs by category

    const groupedData = assets.reduce((acc, asset) => {
      const assetDate = moment(asset.purchase_date);
      const revalued = asset.revalued;
      const revaluationDate = moment(asset.revaluation_date);
      const category = asset.category;
      const disposalDate = moment(asset.disposal_date);
      const disposed = asset.disposed;
      const date2 = moment(ppeDate.start_date);
      const date1 = moment(asset.purchase_date);
      let days_expired = date2.diff(date1, "days");
      let depreciation = days_expired * asset.daily_depreciation;

      // Initialize category if not present
      if (!acc[category]) {
        acc[category] = {
          costAtStart: 0,
          costOfAddition: 0,
          costOfRevalue: 0,
          costOfOriginal: 0,
          costOfDisposal: 0,
          totalCost: 0,
          depreciationAtStart: 0,
          depreciationOfPeriod: 0,
          accumulatedDepreciationOfRevalue: 0,
          depreciationOfDisposal: 0,
        };
      }

      // Calculate costAtStart: add all costs where the date is <= startDate
      if (assetDate < start) {
        acc[category].costAtStart += asset.cost;
      }
      // calculate cost of addition:
      if (assetDate >= start) {
        acc[category].costOfAddition += asset.cost;
      }
      // calculate cost of revalued Asset:
      if (revalued === "true" && revaluationDate <= moment(endDate)) {
        acc[category].costOfRevalue += asset.revalued_amount;
      }
      // calculate cost of revalued Asset:
      if (revalued === "true" && revaluationDate <= moment(endDate)) {
        acc[category].accumulatedDepreciationOfRevalue +=
          asset.accumulated_depreciation;
      }
      // calculate cost of original:
      if (revalued === "true" && revaluationDate <= moment(endDate)) {
        acc[category].costOfOriginal += asset.cost;
      }
      // calculate cost of disposal:
      if (disposed === "true" && disposalDate <= moment(endDate)) {
        acc[category].costOfDisposal += asset.cost;
      }
      // calculate cost of disposal:
      if (disposed === "true" && disposalDate <= moment(endDate)) {
        acc[category].depreciationOfDisposal += asset.accumulated_depreciation;
      }

      // Calculate total: (costAtStart + costOfAddition + costOfRevalue) - costOfOriginal - costOfDisposal
      acc[category].totalCost =
        acc[category].costAtStart +
        acc[category].costOfAddition +
        acc[category].costOfRevalue -
        acc[category].costOfOriginal -
        acc[category].costOfDisposal;

      // totalDepreciation;

      if (moment(asset.purchase_date) > moment(start)) {
        depreciation = 0;
      }
      if (
        moment(asset.purchase_date) < moment(start) &&
        moment(asset.exhaustion_date) > moment(endDate)
      ) {
        const diff = moment(start).diff(moment(asset.purchase_date), "days");
        depreciation = diff * asset.daily_depreciation;
      }
      // when the purchase date is less than the start date and the exhaustion date is also less than the start date;
      if (
        moment(asset.purchase_date) < moment(start) &&
        moment(asset.exhaustion_date) < moment(start)
      ) {
        const diff = moment(asset.exhaustion_date).diff(
          moment(asset.purchase_date),
          "days"
        );
        depreciation = diff * asset.daily_depreciation;
      }
      //! we will handle revaluation here
      if (
        revalued === "true" &&
        moment(asset.revaluation_date) < moment(start) &&
        moment(asset.exhaustion_date) > moment(start)
      ) {
        const diff1 = moment(asset.revaluation_date).diff(
          moment(asset.purchase_date),
          "days"
        );
        const diff2 = moment(start).diff(
          moment(asset.revaluation_date),
          "days"
        );

        const dpn1 = (diff1 - 1) * asset.daily_depreciation;
        // (I maintained the diff2 like that instead of deducting 1 from diff2)
        const dpn2 = diff2 * asset.new_daily_depreciation;

        depreciation = dpn1 + dpn2;
      }
      if (
        revalued === "true" &&
        moment(asset.revaluation_date) < moment(start) &&
        moment(asset.exhaustion_date) < moment(start)
      ) {
        const diff1 = moment(asset.revaluation_date).diff(
          moment(asset.purchase_date),
          "days"
        );
        const diff2 = moment(asset.exhaustion_date).diff(
          moment(asset.revaluation_date),
          "days"
        );
        //! I will come back again
        const dpn1 = (diff1 - 1) * asset.daily_depreciation;
        const dpn2 = diff2 * asset.new_daily_depreciation;
        depreciation = dpn1 + dpn2;
      }
      acc[category].depreciationAtStart += depreciation;

      // depreciation for the period

      // creating a new object and arranging it the way you like.
      acc[category] = {
        costAtStart: acc[category].costAtStart,
        costOfAddition: acc[category].costOfAddition,
        costOfRevalue: acc[category].costOfRevalue,
        costOfOriginal: acc[category].costOfOriginal,
        costOfDisposal: acc[category].costOfDisposal,
        totalCost: acc[category].totalCost, // Ensuring totalCost comes before depreciationAtStart
        depreciationAtStart: acc[category].depreciationAtStart,
        accumulatedDepreciationOfRevalue:
          acc[category].accumulatedDepreciationOfRevalue,
        depreciationOfDisposal: acc[category].depreciationOfDisposal,
      };

      return acc;
    }, {});

    return groupedData;
  }
  const results = filterAndCalculate(
    data,
    ppeDate.start_date,
    ppeDate.end_date
  );

  function forPeriod(
    category,
    purchase_date,
    exhaustion_date,
    cost,
    daily_depreciation,
    total_asset_life,
    revalued,
    new_cost,
    new_daily_depreciation,
    revaluation_date,
    original_daily_depreciation,
    original_cost,
    disposed,
    disposal_date
  ) {
    //  we will check whether an asset is exhausted or not
    let status = moment(exhaustion_date) > moment() ? "Active" : "Exhausted";
    // ----------------------------------------------------------------
    const old_cost = cost;
    const old_asset_life = total_asset_life;
    const date2 = moment();
    const date1 = moment(purchase_date);
    let days_expired = date2.diff(date1, "days");
    let days_unexpired = total_asset_life - days_expired;
    let accum_depre =
      category === "land" ? 0 : days_expired * daily_depreciation;
    let carrying_amount = Math.abs(cost - accum_depre);
    let depreciation = days_expired * daily_depreciation;

    // ! WE DON'T CHARGE DEPN ON LAND
    if (category === "land") {
      status = "Active";
      depreciation = 0;
      days_expired = 0;
      days_unexpired = 0;
      total_asset_life = 0;

      carrying_amount = cost;
      accum_depre = 0;
      exhaustion_date = "";
    }
    if (disposed === "true") {
      cost = 0;
      carrying_amount = 0;
      depreciation = 0;
      accum_depre = 0;
      daily_depreciation = 0;
      days_expired = 0;
      days_unexpired = 0;
      status = "Disposed";
      total_asset_life = 0;
    }

    // ! Revalued Started here -----------------------//!
    //  if the asset has been revalued then we set the cost to the new cost
    cost = revalued === "true" ? new_cost : cost;
    // if the the asset has been revalued then we set the daily depreciation to the new daily depreciation
    daily_depreciation =
      revalued === "true" ? new_daily_depreciation : daily_depreciation;

    // we will check if the asset has been revalued the it accumulated depreciation will start from the day of revaluation to date

    const revaluationDate = moment(revaluation_date);
    const today = moment();
    const difference = today.diff(revaluationDate, "days");
    accum_depre =
      revalued === "true"
        ? new_daily_depreciation * difference
        : days_expired * daily_depreciation;
    //  We need to check carrying amount also for revalued asset
    carrying_amount =
      revalued === "true"
        ? Math.abs(new_cost - accum_depre)
        : Math.abs(cost - accum_depre);
    //! ------------Revalued ended here-------------------------///!

    // !------------start------------------------------------
    // checking if the asset has been exhausted;
    // if (cost - accum_depre < 1) {
    //   carrying_amount = 0;
    // }
    if (status === "Exhausted") {
      days_expired = moment(exhaustion_date).diff(purchase_date, "days");
      days_unexpired = 0;
      const diff = moment(exhaustion_date).diff(revaluation_date, "days");
      accum_depre =
        revalued === "true"
          ? diff * new_daily_depreciation
          : days_expired * daily_depreciation;
      carrying_amount =
        revalued === "true"
          ? Math.abs(new_cost - new_cost)
          : Math.abs(cost - accum_depre);
      // if asset is fully depreciated then it depreciation is set to Zero;
      depreciation = 0;
    }
    // !------------- Exhausted Checking End---------------------------

    // ! things to do when the depreciation start date and end date are all selected--------------//Start

    // we will capture disposed instances here

    // !-----------------START------------------------

    if (
      moment(disposal_date) > moment(ppeDate.end_date) &&
      moment(exhaustion_date) <= moment(ppeDate.end_date) &&
      disposed === "true"
    ) {
      status = "Exhausted";
      total_asset_life = old_asset_life;
      daily_depreciation = original_daily_depreciation;
      days_unexpired = 0;
      disposed = "false";
      cost = old_cost;
      depreciation = 0;
    }

    //! --------END--------------------------

    //! IMPORTANT INSTANCE

    if (
      moment(ppeDate.start_date) <= moment(purchase_date) &&
      moment(ppeDate.end_date) >= moment(exhaustion_date)
    ) {
      days_expired = total_asset_life;
      depreciation = 0;
    }
    // !-----------Important end--------------------
    if (ppeDate.start_date && ppeDate.end_date) {
      if (
        ppeDate.start_date &&
        ppeDate.end_date &&
        moment(purchase_date) < moment(ppeDate.start_date) &&
        moment(exhaustion_date) < moment(ppeDate.start_date) &&
        status === "Exhausted"
      ) {
        accum_depre = cost;
        carrying_amount = 0;
      }
      if (moment(ppeDate.end_date) < moment(purchase_date)) {
        cost = 0;
        carrying_amount = 0;
        accum_depre = 0;
        days_expired = 0;
        days_unexpired = 0;
        depreciation = 0;

        status = "Asset was not available then";
      }
      if (category === "land") {
        depreciation = 0;
        carrying_amount = cost;
        accum_depre = 0;
        daily_depreciation = 0;
        days_expired = 0;
        total_asset_life = 0;
        days_unexpired = 0;
      }
      // check if the depreciation end date is within the exhausted date //! Start------------
      if (moment(ppeDate.end_date) > moment(exhaustion_date)) {
        if (category === "land") {
          depreciation = 0;
          carrying_amount = cost;
          accum_depre = 0;
          daily_depreciation = 0;
          days_expired = 0;
          total_asset_life = 0;
          days_unexpired = 0;
        }
        days_expired = moment(exhaustion_date).diff(
          moment(purchase_date),
          "days"
        );
        days_unexpired = 0;
        accum_depre = days_expired * daily_depreciation;
        carrying_amount = Math.abs(cost - accum_depre);
      }
      // handling of depreciation for the period

      //  we will first check if the purchase date is less than the start date and at the same time the exhaustion date is  greater than the end date
      // then we calculate depreciation with the period;//!start------
      if (
        moment(purchase_date) <= moment(ppeDate.start_date) &&
        moment(exhaustion_date) > moment(ppeDate.end_date)
      ) {
        if (category === "land") {
          depreciation = 0;
          carrying_amount = cost;
          accum_depre = 0;
          daily_depreciation = 0;
          days_expired = 0;
          total_asset_life = 0;
          days_unexpired = 0;
        }
        const diff = moment(ppeDate.end_date).diff(
          moment(ppeDate.start_date),
          "days"
        );
        const cumDateDiff = moment(ppeDate.end_date).diff(
          moment(purchase_date),
          "days"
        );
        accum_depre = cumDateDiff * daily_depreciation;
        carrying_amount = cost - accum_depre;
        days_expired = cumDateDiff;
        days_unexpired = total_asset_life - cumDateDiff;
        depreciation = (diff + 1) * daily_depreciation;
      }
      // ----------!End!----------------//!

      // we will check if the purchase date is less than the start date and the exhaustion date  is greater than the start date and also the exhaustion date is less than the end date//! start

      if (
        moment(purchase_date) < moment(ppeDate.start_date) &&
        moment(exhaustion_date) >= moment(ppeDate.start_date) &&
        moment(exhaustion_date) <= moment(ppeDate.end_date)
      ) {
        if (category === "land") {
          depreciation = 0;
          carrying_amount = cost;
          accum_depre = 0;
          daily_depreciation = 0;
          days_expired = 0;
          total_asset_life = 0;
          days_unexpired = 0;
        }
        const diff = moment(exhaustion_date).diff(
          moment(ppeDate.start_date),
          "days"
        );
        const cumDateDiff = moment(exhaustion_date).diff(
          moment(purchase_date),
          "days"
        );
        status = "Exhausted";
        accum_depre = cumDateDiff * daily_depreciation;
        carrying_amount = cost - accum_depre;
        days_expired = cumDateDiff;
        days_unexpired = total_asset_life - cumDateDiff;
        // we fixed the overcharging here
        depreciation = diff * daily_depreciation;
      }
      // ----------------------//!End---------------------

      // we will check if the purchase date is greater than the start date and the exhaustion date is greater than the end date//!start
      if (
        moment(purchase_date) > moment(ppeDate.start_date) &&
        moment(purchase_date) < moment(ppeDate.end_date) &&
        moment(exhaustion_date) > moment(ppeDate.end_date)
      ) {
        if (category === "land") {
          depreciation = 0;
          carrying_amount = cost;
          accum_depre = 0;
          daily_depreciation = 0;
          days_expired = 0;
          total_asset_life = 0;
          days_unexpired = 0;
        }

        const diff = moment(ppeDate.end_date).diff(
          moment(purchase_date),
          "days"
        );
        const cumDateDiff = moment(ppeDate.end_date).diff(
          moment(purchase_date),
          "days"
        );
        accum_depre = cumDateDiff * daily_depreciation;
        carrying_amount = cost - accum_depre;
        days_expired = cumDateDiff;
        days_unexpired = total_asset_life - cumDateDiff;
        depreciation = (diff + 1) * daily_depreciation;
      }
    }
    // -----------------------End of instance-------------------//!End
    // ! we will be catching all disposed assets instances here

    //  we will check if the disposal happened before the start date

    if (moment(disposal_date) < moment(ppeDate.start_date)) {
      cost = 0;
      carrying_amount = 0;
      depreciation = 0;
      accum_depre = 0;
      daily_depreciation = 0;
      days_expired = 0;
      days_unexpired = 0;
      status = "Disposed";
      total_asset_life = 0;
    }
    // we will check if the disposal date is greater than the start date and is less than or equal to the end date;
    if (
      moment(disposal_date) > moment(ppeDate.start_date) &&
      moment(disposal_date) <= moment(ppeDate.end_date) &&
      status === "Disposed"
    ) {
      // the we will calculate the depn for the period
      const diff = moment(disposal_date).diff(
        moment(ppeDate.start_date),
        "days"
      );
      cost = 0;
      carrying_amount = 0;
      accum_depre = 0;
      daily_depreciation = original_daily_depreciation;
      days_expired = 0;
      days_unexpired = 0;
      status = "Disposed";
      total_asset_life = 0;
      depreciation = (diff + 1) * daily_depreciation;
    }

    //!  we will check revaluation instances here
    // if the revaluation didn't happen before the two selected dates then it has to go back to the previous records.
    if (
      ppeDate.start_date &&
      ppeDate.end_date &&
      moment(revaluation_date) > moment(ppeDate.end_date) &&
      moment(revaluation_date) > moment(ppeDate.end_date) &&
      revalued === "true"
    ) {
      const diff = moment(ppeDate.end_date).diff(
        moment(ppeDate.start_date),
        "days"
      );

      // (diff);
      depreciation = (diff + 1) * original_daily_depreciation;
      cost = original_cost;
      daily_depreciation = original_daily_depreciation;
      accum_depre = days_expired * original_daily_depreciation;
      carrying_amount = Math.abs(cost - accum_depre);
    }
    //  when the the revaluation date is within the selected dates then we will calculate the accumulated depreciation

    if (
      moment(revaluation_date) > moment(ppeDate.start_date) &&
      revalued === "true" &&
      moment(revaluation_date) <= moment(ppeDate.end_date)
    ) {
      const diff = moment(ppeDate.end_date).diff(
        moment(revaluation_date),
        "days"
      );

      accum_depre = diff * new_daily_depreciation;
      carrying_amount = Math.abs(cost - accum_depre);
      const diff2 = moment(revaluation_date).diff(
        moment(ppeDate.start_date),
        "days"
      );
      const depn1 = diff2 * original_daily_depreciation;
      const diff3 = moment(ppeDate.end_date).diff(
        moment(revaluation_date),
        "days"
      );
      const depn2 = diff3 * new_daily_depreciation;
      depreciation = depn1 + depn2;
    }
    // ! When revaluation date is less than start date
    if (
      ppeDate.start_date &&
      ppeDate.end_date &&
      revalued === "true" &&
      moment(exhaustion_date) < moment(ppeDate.start_date) &&
      moment(exhaustion_date) < moment(ppeDate.end_date) &&
      status === "Exhausted"
    ) {
      // (diff);
      depreciation = 0;
      cost = new_cost;
      daily_depreciation = 0;
      accum_depre = new_cost;
      carrying_amount = 0;
    }

    if (
      moment(revaluation_date) < moment(ppeDate.start_date) &&
      status === "Active"
    ) {
      const diff = moment(ppeDate.end_date).diff(
        moment(revaluation_date),
        "days"
      );
      cost = new_cost;
      daily_depreciation = new_daily_depreciation;
      accum_depre = new_daily_depreciation * diff + 1;
      carrying_amount = Math.abs(new_cost - accum_depre);
    }

    if (
      moment(purchase_date) >= moment(ppeDate.start_date) &&
      moment(exhaustion_date) <= moment(ppeDate.end_date)
    ) {
      //  we will check if the purchase date is greater than the start date and the exhaustion date is less than the end date;
      // if both the purchase and the exhaustion date are in btn the start and end date;
      if (category === "land") {
        depreciation = 0;
        carrying_amount = cost;
        accum_depre = 0;
        daily_depreciation = 0;
        days_expired = 0;
        total_asset_life = 0;
        days_unexpired = 0;
      }

      const diff = moment(exhaustion_date).diff(moment(purchase_date), "days");
      depreciation = diff * daily_depreciation;
    }
    if (
      ppeDate.end_date &&
      moment(exhaustion_date) > moment(ppeDate.end_date)
    ) {
      status = "Active";
    }
    // ! Depn start and depn end.-----------------------// End

    return {
      category,
      purchase_date,
      exhaustion_date,
      cost,
      daily_depreciation,
      total_asset_life,
      revalued,
      new_cost,
      new_daily_depreciation,
      revaluation_date,
      original_daily_depreciation,
      original_cost,
      disposed,
      disposal_date,
      depreciation,
    };
  }

  const res = data
    .filter((d) => moment(d.disposal_date) >= moment(ppeDate.end_date))
    .map((data) => {
      return forPeriod(
        data.category,
        data.purchase_date,
        data.exhaustion_date,
        data.cost,
        data.daily_depreciation,
        data.asset_life,
        data.revalued,
        data.new_cost,
        data.new_daily_depreciation,
        data.revaluation_date,
        data.daily_depreciation,
        data.new_cost,
        data.disposed,
        data.disposal_date
      );
    });

  function filterAndCalculateDpn(assets) {
    const groupedData = assets.reduce((acc, asset) => {
      const category = asset.category;
      // Initialize category if not present
      if (!acc[category]) {
        acc[category] = {
          depreciationOfPeriod: 0,
        };
      }
      acc[category].depreciationOfPeriod += asset.depreciation;

      return acc;
    }, {});

    return groupedData;
  }
  const period = filterAndCalculateDpn(res);

  function mergeDepreciationIntoAssets(depreciationData, assetData) {
    // Iterate over the depreciation data
    for (let category in depreciationData) {
      // If the category exists in asset data, add depreciationOfPeriod
      if (assetData[category]) {
        assetData[category].depreciationOfPeriod =
          depreciationData[category].depreciationOfPeriod;
      }
      // Calculate totalDepreciation
      assetData[category].totalDepreciation =
        (assetData[category].depreciationAtStart || 0) +
        (assetData[category].depreciationOfPeriod || 0) -
        (assetData[category].accumulatedDepreciationOfRevalue || 0) -
        (assetData[category].depreciationOfDisposal || 0);
      assetData[category].carryingAmount =
        (assetData[category].totalCost || 0) -
        (assetData[category].totalDepreciation || 0);
    }
    return assetData;
  }

  // Merge the datasets
  const mergedData = mergeDepreciationIntoAssets(period, results);

  function objectToOrderedArray(obj) {
    return Object.entries(obj).map(([category, asset]) => ({
      category,
      costAtStart: asset.costAtStart,
      costOfAddition: asset.costOfAddition,
      costOfRevalue: asset.costOfRevalue,
      costOfOriginal: asset.costOfOriginal,
      costOfDisposal: asset.costOfDisposal, // Before totalCost
      totalCost: asset.totalCost, // After costOfDisposal
      depreciationAtStart: asset.depreciationAtStart,
      accumulatedDepreciationOfRevalue: asset.accumulatedDepreciationOfRevalue,
      depreciationOfPeriod: asset.depreciationOfPeriod,
      depreciationOfDisposal: asset.depreciationOfDisposal, // Before totalDepreciation
      totalDepreciation: asset.totalDepreciation, // After depreciationOfDisposal
      carryingAmount: asset.carryingAmount,
    }));
  }

  // Example usage
  const orderedArray = objectToOrderedArray(mergedData);

  // Extract the categories from the data
  const categories = orderedArray?.map((item) => item.category);

  // Define the rows based on your provided structure
  const tableRows = [
    { label: "Cost At Start", key: "costAtStart" },
    { label: "Cost Of Addition", key: "costOfAddition" },
    { label: "Cost Of Revalued Asset", key: "costOfRevalue" },
    {
      label: "Original Cost Before Revaluation",
      key: "costOfOriginal",
    },
    { label: "Cost Of Disposal", key: "costOfDisposal" },
    { label: "Total Cost", key: "totalCost" },
    { label: "Depreciation At Start", key: "depreciationAtStart" },
    {
      label: "Depreciation Charge For The Period",
      key: "depreciationOfPeriod",
    },
    {
      label: "Accumulated Depreciation for Revalue",
      key: "accumulatedDepreciationOfRevalue",
    },
    { label: "Depreciation of Disposal", key: "depreciationOfDisposal" },
    { label: "Total Depreciation", key: "totalDepreciation" },
    { label: "Carrying Amount", key: "carryingAmount" },
  ];
  return (
    <div style={{ height: "83vh" }}>
      <div
        style={{
          fontSize: "14px",
          // marginBottom: "10px",

          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          columnGap: "10px",
          // alignItems: "center",
          // justifyContent: "space-between",
        }}
      >
        <Typography
          style={{
            color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            fontWeight: "bold",
          }}
          variant="body1"
          htmlFor="start-date"
        >
          Start Date
        </Typography>
        <TextField
          style={{ backgroundColor: "#ccc", borderRadius: "5px" }}
          id="start-date"
          size="small"
          required
          type="date"
          value={ppeDate.start_date}
          onChange={(e) =>
            setPpeDate({ ...ppeDate, start_date: e.target.value })
          }
        />
        <Typography
          style={{
            color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            fontWeight: "bold",
          }}
          variant="body1"
          htmlFor="end-date"
        >
          End Date
        </Typography>
        <TextField
          id="end-date"
          size="small"
          required
          style={{ backgroundColor: "#ccc", borderRadius: "5px" }}
          type="date"
          value={ppeDate.end_date}
          onChange={(e) => setPpeDate({ ...ppeDate, end_date: e.target.value })}
        />
        <Button
          style={{
            backgroundColor: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            marginLeft: "10px",
            textTransform: "none",
          }}
          onClick={handleFetch}
          variant="contained"
        >
          Generate Report
        </Button>
      </div>

      <Divider style={{ marginBottom: "10px" }} />
      <Box width={"30px"}></Box>

      <Paper
        style={{
          backgroundColor: `${mode === "dark" ? "#101418" : "#ffffff"}`,
          color: `${mode === "dark" ? "#fff" : "#000"}`,
          border: "1px solid #ccc",
        }}
        sx={{ width: "100%", height: "100%" }}
      >
        <Button
          onClick={() =>
            exportToExcel(orderedArray, ppeDate.start_date, ppeDate.end_date)
          }
        >
          Export
          <DownloadRoundedIcon />
        </Button>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table
            size="small"
            stickyHeader
            aria-label="sticky table"
            sx={{
              "& tr > *:nth-of-type(1)": {
                left: 0,
                position: "sticky",
                fontFamily: "Poppins",
                fontWeight: 700,
                bgcolor: "#CCCCCC",
                fontSize: "12px",
              },
              "& tbody > *:nth-of-type(6)": {
                left: 0,
                fontWeight: 700,
                bgcolor: "#d1d5db",
              },
              "& tbody > *:nth-of-type(11)": {
                left: 0,
                fontWeight: 700,
                bgcolor: "#d1d5db",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell style={{ backgroundColor: "#ccc" }}>COST</TableCell>
                {categories.map((category, index) => (
                  <TableCell
                    style={{
                      backgroundColor: "#ccc",
                      fontFamily: "Poppins",
                      fontWeight: 700,
                    }}
                    key={index}
                  >
                    {category.toUpperCase()}
                  </TableCell>
                ))}
                <TableCell
                  style={{
                    backgroundColor: "#ccc",
                    fontFamily: "Poppins",
                    fontWeight: 700,
                  }}
                >
                  TOTAL
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell>{row.label}</TableCell>
                  {orderedArray.map((item, colIndex) => (
                    <TableCell
                      style={{
                        color:
                          mode === "light"
                            ? "rgba(0,0,0,.85)"
                            : "rgba(255,255,255,0.65)",
                      }}
                      key={colIndex}
                    >
                      {(item[row.key] !== undefined
                        ? item[row.key]
                        : 0
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                  ))}
                  <TableCell
                    style={{
                      color:
                        mode === "light"
                          ? "rgba(0,0,0,.85)"
                          : "rgba(255,255,255,0.65)",
                    }}
                  >
                    {orderedArray
                      .reduce((sum, item) => sum + (item[row.key] || 0), 0)
                      .toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
